import { useState, useEffect, useRef } from "react";
import { ArrowLeft, Activity, Droplets, Clock, FlaskConical, Info, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { GLUCOSE_READING_TOOLTIPS } from "../../utils/glucoseReadingsTooltips";
import {
  EMPTY_GLUCOSE_READINGS,
  glucoseReadingsUiHasAny,
  type GlucoseReadingsUi,
} from "../../utils/glucoseReadingsModel";
import glucoseReadingsService from "../../services/GlucoseReadingsService";
import { Button } from "../../components/ui/button";
import InfoSheet from "../../components/common/InfoSheet";
import welcomeBg from "../../assets/homebg.png";

function getNowDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function getNowTime(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function readLocalGlucoseReadings(): GlucoseReadingsUi {
  try {
    const stored = localStorage.getItem("glucose_readings");
    return stored ? JSON.parse(stored) : { ...EMPTY_GLUCOSE_READINGS };
  } catch {
    return { ...EMPTY_GLUCOSE_READINGS };
  }
}

type DateTimeReading = {
  key: "cgm" | "fingerPrick" | "fasting";
  dateKey: "cgmDate" | "fingerPrickDate" | "fastingDate";
  timeKey: "cgmTime" | "fingerPrickTime" | "fastingTime";
  label: string;
  tooltip: string;
  icon: typeof Activity;
  iconColor: string;
  iconBg: string;
  accentBorder: string;
  accentHeader: string;
  dateBg: string;
  unit: string;
  placeholder: string;
};

type PlainReading = {
  key: "hba1c";
  label: string;
  tooltip: string;
  icon: typeof Activity;
  iconColor: string;
  iconBg: string;
  unit: string;
  placeholder: string;
  step: string;
};

const GlucoseReadingsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hydratedFromApi = useRef(false);

  const [formData, setFormData] = useState<GlucoseReadingsUi>(() => readLocalGlucoseReadings());

  const { data: apiGlucoseReadings, isFetched } = useQuery({
    queryKey: ["glucoseReadings"],
    queryFn: () => glucoseReadingsService.getReadings(),
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (!isFetched || hydratedFromApi.current) return;
    hydratedFromApi.current = true;
    if (apiGlucoseReadings && glucoseReadingsUiHasAny(apiGlucoseReadings)) {
      setFormData(apiGlucoseReadings);
    } else {
      setFormData(readLocalGlucoseReadings());
    }
  }, [isFetched, apiGlucoseReadings]);

  const [saving, setSaving] = useState(false);

  const setReadingToNow = (
    dateKey: DateTimeReading["dateKey"],
    timeKey: DateTimeReading["timeKey"]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [dateKey]: getNowDate(),
      [timeKey]: getNowTime(),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await glucoseReadingsService.saveReadings(formData, {
        showNotification: true,
      });
      if (saved) {
        localStorage.setItem("glucose_readings", JSON.stringify(saved));
        await queryClient.invalidateQueries({ queryKey: ["glucoseReadings"] });
        setTimeout(() => navigate(-1), 800);
      } else {
        localStorage.setItem("glucose_readings", JSON.stringify(formData));
        message.warning("Saved on this device only. Could not sync to server.");
        setTimeout(() => navigate(-1), 800);
      }
    } catch {
      message.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const dateTimeReadings: DateTimeReading[] = [
    {
      key: "cgm",
      dateKey: "cgmDate",
      timeKey: "cgmTime",
      label: "CGM",
      tooltip: GLUCOSE_READING_TOOLTIPS.cgm,
      icon: Activity,
      iconColor: "text-indigo-500",
      iconBg: "bg-indigo-100 dark:bg-indigo-900/30",
      accentBorder: "border-indigo-200 dark:border-indigo-700/50",
      accentHeader: "bg-indigo-50/60 dark:bg-indigo-900/20",
      dateBg: "bg-indigo-50 dark:bg-indigo-900/20",
      unit: "mg/dL",
      placeholder: "Enter CGM value",
    },
    {
      key: "fingerPrick",
      dateKey: "fingerPrickDate",
      timeKey: "fingerPrickTime",
      label: "Finger Prick",
      tooltip: GLUCOSE_READING_TOOLTIPS.fingerPrick,
      icon: Droplets,
      iconColor: "text-emerald-500",
      iconBg: "bg-emerald-100 dark:bg-emerald-900/30",
      accentBorder: "border-emerald-200 dark:border-emerald-700/50",
      accentHeader: "bg-emerald-50/60 dark:bg-emerald-900/20",
      dateBg: "bg-emerald-50 dark:bg-emerald-900/20",
      unit: "mg/dL",
      placeholder: "Enter Finger Prick value",
    },
    {
      key: "fasting",
      dateKey: "fastingDate",
      timeKey: "fastingTime",
      label: "Fasting",
      tooltip: GLUCOSE_READING_TOOLTIPS.fasting,
      icon: Clock,
      iconColor: "text-orange-500",
      iconBg: "bg-orange-100 dark:bg-orange-900/30",
      accentBorder: "border-orange-200 dark:border-orange-700/50",
      accentHeader: "bg-orange-50/60 dark:bg-orange-900/20",
      dateBg: "bg-orange-50 dark:bg-orange-900/20",
      unit: "mg/dL",
      placeholder: "Enter Fasting value",
    },
  ];

  const hba1cReading: PlainReading = {
    key: "hba1c",
    label: "HbA1c",
    tooltip: GLUCOSE_READING_TOOLTIPS.hba1c,
    icon: FlaskConical,
    iconColor: "text-purple-500",
    iconBg: "bg-purple-100 dark:bg-purple-900/30",
    unit: "%",
    placeholder: "Enter HbA1c value",
    step: "0.1",
  };

  return (
    <div className="scrollbar-hide relative mx-auto flex h-dvh max-h-dvh w-full min-w-0 max-w-[400px] flex-col overflow-hidden bg-background">
      {/* Blurred Background */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-background/80" />

      <div className="scrollbar-hide relative z-10 flex min-h-0 flex-1 flex-col space-y-4 overflow-y-auto px-3 pb-[max(1rem,env(safe-area-inset-bottom,0px))] pt-2 sm:space-y-4 sm:px-4 sm:pb-20 sm:pt-3">
        {/* Header — border-0 / ring-0 avoids stray strokes from global + Ant styles */}
        <div className="relative flex items-center justify-center border-0 pt-1 outline-none ring-0 sm:pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="absolute left-0 h-11 w-11 rounded-xl border-0 bg-card/80 shadow-none outline-none ring-0 hover:bg-card sm:left-0 sm:h-10 sm:w-10"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="line-clamp-2 max-w-[70%] border-0 text-center text-title3 font-bold leading-tight text-foreground outline-none ring-0 sm:max-w-none sm:text-title2">
            Glucose Readings
          </h1>
        </div>

        {/* Hero / guidance — borders removed (shadow only); no inner horizontal rule */}
        <div className="overflow-hidden rounded-3xl border-0 bg-gradient-to-br from-white/95 via-indigo-50/80 to-sky-50/90 shadow-sm outline-none ring-0">
          <div className="border-0 px-4 py-4 outline-none">
            <div className="mb-2 inline-flex items-center rounded-full border border-indigo-200 bg-white/80 px-3 py-1">
              <span className="text-caption2 font-bold uppercase tracking-widest text-indigo-600">
                Today&apos;s Manual Entry
              </span>
            </div>
            <h2 className="text-headline font-bold text-foreground">
              Add a new glucose value for today whenever you measure it.
            </h2>
            <p className="mt-1 text-footnote leading-snug text-muted-foreground">
              The form opens with today&apos;s date and current time so the user can quickly log a fresh reading, then edit the timestamp only if the reading was taken earlier.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 px-4 py-3 sm:grid-cols-4">
            <div className="rounded-2xl border border-white/80 bg-white/80 px-3 py-2.5">
              <p className="text-caption2 uppercase tracking-widest text-muted-foreground">Records</p>
              <p className="mt-1 text-subhead font-semibold text-foreground">Date-wise history</p>
            </div>
            <div className="rounded-2xl border border-white/80 bg-white/80 px-3 py-2.5">
              <p className="text-caption2 uppercase tracking-widest text-muted-foreground">Default</p>
              <p className="mt-1 text-subhead font-semibold text-foreground">Today + current time</p>
            </div>
            <div className="rounded-2xl border border-white/80 bg-white/80 px-3 py-2.5">
              <p className="text-caption2 uppercase tracking-widest text-muted-foreground">Editable</p>
              <p className="mt-1 text-subhead font-semibold text-foreground">Date and time</p>
            </div>
            <div className="rounded-2xl border border-white/80 bg-white/80 px-3 py-2.5">
              <p className="text-caption2 uppercase tracking-widest text-muted-foreground">Sync</p>
              <p className="mt-1 text-subhead font-semibold text-foreground">API-backed save</p>
            </div>
          </div>
        </div>

        {/* Info Banner — borderless so no blue stroke clashes with globals */}
        <div className="rounded-2xl border-0 bg-blue-50/85 p-4 shadow-sm outline-none ring-0 dark:bg-blue-900/20">
          <p className="text-footnote leading-relaxed text-blue-800 dark:text-blue-200 sm:text-blue-700 sm:dark:text-blue-300">
            Improve prediction accuracy by providing your current glucose level. If you are non-diabetic, you can skip this and we will use the normal glucose baseline of 95 mg/dL.
          </p>
          <p className="mt-2 text-caption1 font-medium text-blue-700 dark:text-blue-200">
            Want to log a fresh reading for today? Enter the value and save it with the prefilled timestamp.
          </p>
        </div>

        {/* CGM / Finger Prick / Fasting — each with their own date & time */}
        <div className="space-y-4">
          {dateTimeReadings.map((r) => {
            const Icon = r.icon;
            return (
              <div
                key={r.key}
                className={`overflow-hidden rounded-3xl border bg-card/95 shadow-sm backdrop-blur-sm ${r.accentBorder}`}
              >
                {/* Card header */}
                <div
                  className={`flex items-start justify-between px-4 py-3 ${r.accentHeader}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${r.iconBg}`}
                    >
                      <Icon className={`h-4 w-4 ${r.iconColor}`} />
                    </div>
                    <div>
                      <span className="text-subhead font-semibold text-foreground">
                        {r.label}{" "}
                        <span className="text-footnote font-normal text-muted-foreground">
                          ({r.unit})
                        </span>
                      </span>
                      <p className="mt-1 text-caption1 leading-snug text-muted-foreground">
                        Enter the reading and keep the capture time for accurate history.
                      </p>
                    </div>
                  </div>
                  <InfoSheet
                    title={r.label}
                    ariaLabel={`About ${r.label}`}
                    trigger={
                      <button
                        type="button"
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted/70 hover:text-foreground active:bg-muted"
                        aria-label={`About ${r.label}`}
                      >
                        <Info className="h-4 w-4" />
                      </button>
                    }
                  >
                    <p>{r.tooltip}</p>
                  </InfoSheet>
                </div>

                {/* Value input */}
                <div className="px-4 pt-3">
                  <div className="rounded-2xl border border-border bg-background/90 p-3 shadow-sm">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-caption2 uppercase tracking-widest text-muted-foreground">
                          New value for today
                        </span>
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-caption2 font-semibold text-primary">
                          Quick log
                        </span>
                      </div>
                      <span className="text-caption1 font-medium text-muted-foreground">
                        {r.unit}
                      </span>
                    </div>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="1"
                      placeholder={r.placeholder}
                      value={formData[r.key]}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          [r.key]: e.target.value,
                        }))
                      }
                      className="w-full border-0 bg-transparent px-0 py-0 text-callout font-semibold text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:ring-0"
                    />
                  </div>
                </div>

                {/* Date & Time row */}
                <div className="mx-4 mt-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-caption2 uppercase tracking-widest text-muted-foreground">
                      Record timestamp
                    </p>
                    <p className="mt-1 text-caption1 text-muted-foreground">
                      Today&apos;s date and current time are already filled in. Change them only if this reading belongs to another time.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReadingToNow(r.dateKey, r.timeKey)}
                    className="shrink-0 rounded-full border border-border bg-background px-3 py-1.5 text-caption1 font-semibold text-foreground transition-colors hover:bg-muted"
                  >
                    Reset to now
                  </button>
                </div>

                <div className={`mx-4 mb-4 mt-3 grid grid-cols-2 gap-3 rounded-2xl p-3 ${r.dateBg}`}>
                  {/* Date */}
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1 px-0.5">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      <span className="text-caption2 font-medium uppercase tracking-wide text-muted-foreground">
                        Date
                      </span>
                    </div>
                    <input
                      type="date"
                      value={formData[r.dateKey]}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          [r.dateKey]: e.target.value,
                        }))
                      }
                      className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-footnote text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  {/* Time */}
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1 px-0.5">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      <span className="text-caption2 font-medium uppercase tracking-wide text-muted-foreground">
                        Time
                      </span>
                    </div>
                    <input
                      type="time"
                      value={formData[r.timeKey]}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          [r.timeKey]: e.target.value,
                        }))
                      }
                      className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-footnote text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* HbA1c — no date/time needed */}
        <div className="flex min-w-0 touch-manipulation items-start gap-3 rounded-3xl border border-border bg-card/95 p-4 shadow-sm backdrop-blur-sm sm:gap-3 sm:p-4">
          <div
            className={`mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${hba1cReading.iconBg}`}
          >
            <FlaskConical className={`h-5 w-5 ${hba1cReading.iconColor}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-3 flex min-w-0 items-start gap-1.5">
              <div className="min-w-0 flex-1">
                <label className="min-w-0 flex-1 truncate text-subhead font-semibold text-foreground">
                  {hba1cReading.label} <span className="text-footnote font-normal text-muted-foreground">({hba1cReading.unit})</span>
                </label>
                <p className="mt-1 text-caption1 text-muted-foreground">
                  Add your lab HbA1c value when available to keep long-term glucose records complete.
                </p>
              </div>
              <InfoSheet
                title={hba1cReading.label}
                ariaLabel={`About ${hba1cReading.label}`}
                trigger={
                  <button
                    type="button"
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted/70 hover:text-foreground active:bg-muted sm:h-9 sm:w-9"
                    aria-label={`About ${hba1cReading.label}`}
                  >
                    <Info className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                  </button>
                }
              >
                <p>{hba1cReading.tooltip}</p>
              </InfoSheet>
            </div>
            <div className="rounded-2xl border border-border bg-background/90 p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-caption2 uppercase tracking-widest text-muted-foreground">
                  Lab value
                </span>
                <span className="text-caption1 font-medium text-muted-foreground">
                  {hba1cReading.unit}
                </span>
              </div>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step={hba1cReading.step}
                placeholder={hba1cReading.placeholder}
                value={formData.hba1c}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    hba1c: e.target.value,
                  }))
                }
                className="w-full border-0 bg-transparent px-0 py-0 text-callout font-semibold text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:ring-0"
              />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card/90 px-4 py-3 shadow-sm">
          <p className="text-caption2 uppercase tracking-widest text-muted-foreground">
            Save today&apos;s entry
          </p>
          <p className="mt-1 text-footnote text-muted-foreground">
            Enter the new value, confirm the prefilled date and time, then save so today&apos;s glucose record stays available for future review.
          </p>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="mt-auto h-12 w-full shrink-0 rounded-2xl bg-gradient-primary text-callout font-semibold text-primary-foreground shadow-primary hover:opacity-90 disabled:opacity-50 sm:text-subhead"
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

export default GlucoseReadingsPage;
